import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CheckboxModule } from 'primeng/checkbox';
import { AccordionModule } from 'primeng/accordion';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { catchError, forkJoin, of } from 'rxjs';
import { UserAdminService } from '../../../core/services/user-admin.service';
import { AdminUserResponse, Permission } from '../../../core/models';
import { AppDialog } from '../../../shared/ui/dialog/app-dialog';

// Regroupement purement visuel : dérivé du préfixe du code (USERS_READ -> "USERS"), pas
// d'un champ dédié côté API — le catalogue reste une simple liste plate côté backend.
const GROUP_LABELS: Record<string, string> = {
  USERS: 'Utilisateurs',
  PROPERTIES: 'Biens immobiliers',
  PLANS: 'Plans & Tarifs',
  DOCUMENTS: "Documents d'inscription",
  ADS: 'Publicités',
  SYSTEM: 'Comptes système',
};

function groupKeyFor(code: string): string {
  return code.startsWith('SYSTEM_') ? 'SYSTEM' : code.split('_')[0];
}

interface PermissionGroup {
  key: string;
  label: string;
  permissions: Permission[];
}

@Component({
  selector: 'app-user-permissions',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, ToggleSwitchModule, CheckboxModule,
    AccordionModule, ProgressSpinnerModule, AppDialog],
  templateUrl: './user-permissions.html',
  styleUrl: './user-permissions.scss'
})
export class UserPermissionsComponent {
  private svc   = inject(UserAdminService);
  private toast = inject(MessageService);

  visible = model<boolean>(false);
  user = input<AdminUserResponse | null>(null);
  updated = output<void>();

  loading = signal(false);
  saving  = signal(false);
  catalog = signal<Permission[]>([]);
  granted = signal<Set<string>>(new Set());

  groups = computed<PermissionGroup[]>(() => {
    const byKey = new Map<string, Permission[]>();
    for (const perm of this.catalog()) {
      const key = groupKeyFor(perm.code);
      (byKey.get(key) ?? byKey.set(key, []).get(key)!).push(perm);
    }
    return Array.from(byKey.entries()).map(([key, permissions]) => ({
      key, label: GROUP_LABELS[key] ?? key, permissions
    }));
  });

  // Tous les groupes ouverts par défaut (peu d'items chacun, l'accordéon sert à organiser
  // visuellement, pas à cacher par défaut).
  openGroups = computed(() => this.groups().map(g => g.key));

  get isSuperAdmin() { return this.user()?.role === 'ROLE_SUPER_ADMIN'; }

  get allGranted(): boolean {
    return this.catalog().length > 0 && this.catalog().every(p => this.granted().has(p.code));
  }

  get allGrantedIndeterminate(): boolean {
    const g = this.granted();
    return g.size > 0 && !this.allGranted;
  }

  constructor() {
    effect(() => {
      const u = this.user();
      if (this.visible() && u) {
        this.load(u.id);
      }
    });
  }

  private load(userId: string) {
    if (this.isSuperAdmin) return;
    this.loading.set(true);
    forkJoin({
      catalog: this.svc.getPermissionCatalog().pipe(catchError(() => of([] as Permission[]))),
      granted: this.svc.getUserPermissions(userId).pipe(catchError(() => of([] as string[])))
    }).subscribe(({ catalog, granted }) => {
      this.catalog.set(catalog);
      this.granted.set(new Set(granted));
      this.loading.set(false);
    });
  }

  isGranted(code: string): boolean {
    return this.granted().has(code);
  }

  toggle(code: string, checked: boolean) {
    this.granted.update(set => {
      const next = new Set(set);
      if (checked) next.add(code); else next.delete(code);
      return next;
    });
  }

  isGroupFullyGranted(group: PermissionGroup): boolean {
    return group.permissions.every(p => this.granted().has(p.code));
  }

  isGroupIndeterminate(group: PermissionGroup): boolean {
    const someGranted = group.permissions.some(p => this.granted().has(p.code));
    return someGranted && !this.isGroupFullyGranted(group);
  }

  toggleGroup(group: PermissionGroup, checked: boolean) {
    this.granted.update(set => {
      const next = new Set(set);
      for (const p of group.permissions) {
        if (checked) next.add(p.code); else next.delete(p.code);
      }
      return next;
    });
  }

  toggleAll(checked: boolean) {
    this.granted.set(checked ? new Set(this.catalog().map(p => p.code)) : new Set());
  }

  close() {
    this.visible.set(false);
  }

  save() {
    const u = this.user();
    if (!u) return;
    this.saving.set(true);
    // Endpoint void (204 No Content) : Angular résout l'observable avec `null` en cas
    // de succès, donc `null` ne peut pas servir de sentinelle d'erreur ici (contrairement
    // au pattern catchError(() => of(null)) utilisé ailleurs pour des GET qui renvoient
    // un vrai DTO) — next()/error() séparés pour lever toute ambiguïté.
    this.svc.updateUserPermissions(u.id, Array.from(this.granted())).subscribe({
      next: () => {
        this.saving.set(false);
        this.visible.set(false);
        this.toast.add({ severity: 'success', summary: 'Permissions mises à jour', detail: `« ${u.firstName} ${u.lastName} »`, life: 4000 });
        this.updated.emit();
      },
      error: () => {
        this.saving.set(false);
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: "La mise à jour des permissions a échoué.", life: 5000 });
      }
    });
  }
}
